import { takeLatest, put, call } from 'redux-saga/effects';
import axios from 'axios';

// constants for endpoints
import {
  AUTHENTICATION,
  BONUS_API,
} from '../../../components/global/Server/endpoints';

// constants for actions
import {
  AUTH_LOGIN,
  AUTH_LOGOUT,
  GET_EMPLOYEES,
  GET_ONLINE_EMPLOYEES,
  GET_USERS,
  REGISTER_USER,
} from '../../constants/auth';
import {
  DELETE_RFID,
  PROCESS_RFID,
  REGISTER_RFID,
  UPLOAD_BONUS_PHOTO,
} from '../../constants/userManagement';

// secondary actions
import {
  requestStatus,
  clearStatus,
  showErrorMessage,
  hideError,
  showLicenseDialog,
} from '../../actions/global';
import { fetchLinkedUsers, storeRFID } from '../../actions/userManagement';
import {
  storeEmployees,
  storeOnlineEmployees,
  storeToken,
  storeUsers,
} from '../../actions/auth';
import { HTTP_SUCCESS } from '../../constants/global';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../pages/Dashboard/constants';
import AuthUtil from '../../AuthUtil';

function* getUserInfo(data, token) {
  return yield axios
    .post(AUTHENTICATION.GET_USER_INFO, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((response) => response.data);
}

function* request(username, password) {
  return yield axios
    .post(AUTHENTICATION.AUTH_LOGIN, {
      username,
      password,
    })
    .then((res) => res.data)
    .catch((error) => {
      let response = {};
      if (error.response) {
        response.status = error.response.status;
        response.statusText = error.response.statusText;
        response.data = error.response.data;
      }
      return response;
    });
}

function* authLogout() {
  yield put(hideError());
  try {
    yield call(() => AuthUtil.postRequest(AUTHENTICATION.AUTH_LOGOUT, {}));
    yield localStorage.clear();
    window.location.replace('/login');
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* authLogin(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      request(action.username, action.password)
    );
    if (response.access_token) {
      const additionalInfo = yield call(() =>
        getUserInfo({ username: action.username }, response.access_token)
      );

      localStorage.setItem('token', response.access_token);

      const userInfo = {
        username: response.username,
        authority: response.roles,
        firstname: additionalInfo.firstname,
        lastname: additionalInfo.lastname,
        userId: additionalInfo.userId,
        company: additionalInfo.companyId,
        linkedUsers: additionalInfo.linkedUsers,
        routes: additionalInfo.routes,
        userType: additionalInfo.userType,
        employeeColor: additionalInfo.employeeColor,
        companyIds: additionalInfo.companyIds,
        selectedLocations: additionalInfo.selectedLocations,
        defaultLocationId: additionalInfo.defaultLocationId,
        selectedLocationId: additionalInfo.companyId,
        isOwner: additionalInfo.isOwner,
        selectedLocationName: additionalInfo.selectedLocationName,
        planName: additionalInfo.planName,
      };

      yield put(storeToken(response.access_token, userInfo));

      // Define who counts as an Admin
      const isAdmin =
        response.roles.includes('ROLE_ADMIN') ||
        response.roles.includes('ROLE_MASTER_ADMIN');

      // A user is a *dedicated* security person only if their role
      // bundle is the Security preset — i.e. they have ROLE_SECURITY
      // but not the operational dashboard roles that Manager / Super-
      // visor / Employee always get (ROLE_CUSTOMER from the Dashboard
      // section). Without this check, Managers and Supervisors who
      // have the Security section enabled would also be redirected
      // to /security on login.
      const hasSecurityRole = response.roles.includes('ROLE_SECURITY');
      const hasCustomerAccess = response.roles.includes('ROLE_CUSTOMER');
      const isDedicatedSecurity = hasSecurityRole && !hasCustomerAccess;

      if (isDedicatedSecurity && !isAdmin) {
        // Dedicated Security role: land on the security dashboard.
        yield action.history.push('/security');
      } else if (isAdmin) {
        // Admins go to the main dashboard/home even if they have the security role
        yield action.history.push('/');
      } else {
        // Everyone else
        yield action.history.push('/');
      }
    } else {
      if (response.data.includes('Your company account has expired.')) {
        yield put(showLicenseDialog());
      }
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* registerUser(action) {
  yield call(clearStatus);

  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.AUTH_REGISTER, action.newUser)
    );

    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === 200,
    };

    yield put(requestStatus(status));
    if (status.success && action.newUser.is_linked_user) {
      const userAction = {
        locationId: action.newUser.selectedLocationId,
      };
      yield put(fetchLinkedUsers(userAction));
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(response.data.success, response.data.message);
      }
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback(false, SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getUsersSaga() {
  try {
    const response = yield call(() =>
      AuthUtil.getRequest(AUTHENTICATION.GET_USERS)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    yield put(storeUsers(response.data));
    yield put(requestStatus(status));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getEmployeesSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.GET_EMPLOYEES, action.data)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    yield put(storeEmployees(response.data));
    yield put(requestStatus(status));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* getOnlineEmployeesSaga(action) {
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.GET_ONLINE_EMPLOYEES, action.data)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };
    yield put(storeOnlineEmployees(response.data));
    yield put(requestStatus(status));
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* registerRFIDSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.REGISTER_RFID, action.data)
    );
    if (response.data.success) {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(true, response.data.message);
      }
      yield put(storeRFID(response.data.user));
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(false, response.data.message);
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback(false, SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* processRFIDSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.PROCESS_RFID, action.data)
    );
    if (action.callback && typeof action.callback === 'function') {
      action.callback(response.data.success, response.data.message);
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback('error', SERVER_FAILED_ERROR_MESSAGE);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* uploadBonusPhotoSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postUploadRequest(
        BONUS_API.UPLOAD_BONUS_PHOTO,
        action.data,
        (progress) => {
          if (action.dispatchProgress) action.dispatchProgress(progress);
        }
      )
    );
    if (response.data.success) {
      if (action.callback) action.callback(true, 'Saved');
      if (action.dispatchProgress) action.dispatchProgress(0); // reset progress
    }
  } catch (error) {
    if (action.callback) action.callback(false, 'Server failed');
    yield put(showErrorMessage('Server failed'));
    if (action.dispatchProgress) action.dispatchProgress(0); // reset progress
  }
}

function* deleteRFIDSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(AUTHENTICATION.DELETE_RFID, action.data)
    );
    if (response.data.success) {
      yield put(storeRFID(response.data.user));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

export default function* authSagas() {
  yield takeLatest(AUTH_LOGIN, authLogin);
  yield takeLatest(REGISTER_USER, registerUser);
  yield takeLatest(AUTH_LOGOUT, authLogout);
  yield takeLatest(GET_USERS, getUsersSaga);
  yield takeLatest(REGISTER_RFID, registerRFIDSaga);
  yield takeLatest(DELETE_RFID, deleteRFIDSaga);
  yield takeLatest(PROCESS_RFID, processRFIDSaga);
  yield takeLatest(GET_EMPLOYEES, getEmployeesSaga);
  yield takeLatest(GET_ONLINE_EMPLOYEES, getOnlineEmployeesSaga);
  yield takeLatest(UPLOAD_BONUS_PHOTO, uploadBonusPhotoSaga);
}
